/* eslint-disable eslint-comments/no-unlimited-disable */
/* eslint-disable */
import * as React from "react";
import { useSelector } from "react-redux";
import * as moment from "moment";
import { Container, Loader, Segment, Grid, Button, List, Divider } from "semantic-ui-react";
import { createValidator } from "not-valid";
import { uniq, flatten, values, debounce } from "lodash";

import { EmployeeRecord, OhCase, AnsweredQuestion, AnsweredQuestionWithId, ConsultationDetails } from "@teamprevent/common/model";
import { FitnessOutcome, PhysiotherapyReferralReportDetails } from "@teamprevent/common/physiotherapyReferralReport/models";

import * as questionIds from "../questionIds";
import {
    PhysiotherapyReferralReportModel,
    PHYSIOTHERAPY_REFERRAL_REPORT_KEY,
    reportTypeOptions,
    workClassificationOptions,
    fitnessOutcomeOptions,
    adjustmentOptions,
    adjustmentsRecommendedOptions,
    phasedReturnOptions,
    workStatusAtInitialAssessmentOptions,
    workStatusAtDischargeOptions,
    outcomeOptions,
    managementDetailsOptions,
    ReportType,
    PhysiotherapyReferralReportFormSections as sections
} from "../models";
import { AccordionQuestionnaireContainer } from "../../questionnaires/components/AccordionQuestionnaireContainer";
import { AccordionQuestionnaireSection } from "../../questionnaires/components/AccordionQuestionnaireSection";
import { TextQuestion } from "../../questionnaires/components/questions/TextQuestion";
import { OldDateQuestion } from "../../questionnaires/components/questions/OldDateQuestion";
import { QuestionConfig } from "../../questionnaires/questionnaireConfig";
import { masterConfig } from "../masterConfig";
import { DropdownQuestion } from "../../questionnaires/components/questions/DropdownQuestion";
import { MultiCheckboxQuestion, MultiCheckbox } from "../../questionnaires/components/questions/MultiCheckboxQuestion";
import { RadioButtonQuestion, RadioButton } from "../../questionnaires/components/questions/RadioButtonQuestion";
import { QuestionnaireAnswer } from "../../questionnaires/components/QuestionnaireAnswer";
import { SavingIndicator } from "../../components/SavingIndicator";
import { QuestionnairesState, QuestionnaireTypes } from "../../questionnaires/model";
import { usePrevious } from "../../customHooks";
import { useHrAndLineManagers } from "../../reportsCommon/reportsCommon";
import { EmployeeReleaseReportWithCommentsInfo } from "../../reportsCommon/components/EmployeeReleaseReportWithCommentsInfo";
import { ConsentWithdrawnInfo } from "../../reportsCommon/components/ConsentWithdrawnInfo";

export interface PhysiotherapyReferralReportDispatchProps {
    loadQuestionnaire: (ohCaseId: string, taskId: string, customerId: string) => Promise<void>;
    submitQuestionnaire?: (report: PhysiotherapyReferralReportModel) => Promise<boolean>;
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }) => void;
    loadEmployee: (employeeRecordId: string, customerId: string) => Promise<void> | null;
    loadConsultationDetails: (ohCaseId: string, customerId: string) => Promise<void> | null;
    saveReport: (ohCaseId: string, taskId: string, report: PhysiotherapyReferralReportModel) => Promise<void> | null;
    clearQuestionnaire: () => void;
    onCompletion: () => void;
    submitInvalidQuestionnaire: () => void;
    loadReportDetails?: (ohCaseId: string, taskId: string) => Promise<PhysiotherapyReferralReportDetails>;
}

interface OwnProps {
    readonly: boolean;
    loading?: boolean;
    customerId: string;
    ohCaseId: string;
    taskId: string;
    ohCase: OhCase | null;
    employeeRecord: EmployeeRecord | null;
    report: PhysiotherapyReferralReportModel;
    consultationAndReferralDetails: ConsultationDetails;
    extraSections?: { title: string; id: string; content: React.ReactNode }[];
    extraQuestionContent?: { [questionId: string]: React.ReactNode };
    reportDetails?: PhysiotherapyReferralReportDetails;
}

type AllProps = OwnProps & PhysiotherapyReferralReportDispatchProps;

export const PhysiotherapyReferralReportV2: React.FunctionComponent<AllProps> = (props: AllProps) => {

    const [saving, setSaving] = React.useState<boolean>(false);

    const saveDebounced = React.useCallback(
        debounce(async (report: PhysiotherapyReferralReportModel) => {
            try {
                if (props.readonly === false) {
                    await props.saveReport(props.ohCaseId, props.taskId, report);
                }
            } catch (ex) {
                if (ex.status === 400) {
                    loadQuestionnaire();
                } else {
                    throw ex;
                }
            }
            finally {
                setSaving(false);
            }
        }, 1500),
        [props.readonly, props.ohCaseId, props.taskId]
    );

    const formQuestions: AnsweredQuestionWithId[] = useSelector((state: { questionnaires: QuestionnairesState }) =>
        state.questionnaires[PHYSIOTHERAPY_REFERRAL_REPORT_KEY]
            ? uniq(flatten(state.questionnaires[PHYSIOTHERAPY_REFERRAL_REPORT_KEY].sections.map(section => values(section.questions))))
            : []
    );

    const previousQuestions: AnsweredQuestionWithId[] = usePrevious(formQuestions) ?? [];

    const employeeSeenQuestionId = "EmployeeSeen";

    const addressString = props.employeeRecord && [
        props.employeeRecord.addressLine1,
        props.employeeRecord.addressLine2,
        props.employeeRecord.addressLine3,
        props.employeeRecord.county,
        props.employeeRecord.city,
        props.employeeRecord.county,
        props.employeeRecord.postCode
    ].filter(addressPart => !!addressPart).join("\n");

    React.useEffect(() => {
        props.clearQuestionnaire();
    }, []);

    React.useEffect(() => {
        (async () => {
            if (props.readonly === false) {
                const answersUpdated = previousQuestions?.length > 0 && formQuestions.some((q, i) => q !== previousQuestions[i]);
                if (answersUpdated) {
                    setSaving(true);
                    await saveDebounced(props.report);
                }
            }
        })();
    }, [props.readonly, formQuestions, previousQuestions]);

    React.useEffect(() => {
        (async () => {
            if (props.ohCase) {
                await props.loadEmployee(props.ohCase.employeeRecordId, props.customerId);
            }
        })();
    }, [props.ohCase]);

    React.useEffect(() => {
        (async () => {
            await props.loadConsultationDetails(props.ohCaseId, props.taskId);
        })();
    }, [props.taskId, props.ohCaseId]);

    const getAnswerToQuestionWithId = (id: string) => {
        return props.report.questions.find(question => question.id === id)?.answer;
    };

    const loadQuestionnaire = async () => {
        await props.loadQuestionnaire(props.ohCaseId, props.taskId, props.customerId);
    };

    const getQuestionConfig = (questionId: string): QuestionConfig => {
        return masterConfig.find(c => c.id === questionId) || { id: questionId, disabled: true };
    };

    const isDateInThePast = (value: moment.Moment) => {
        return value?.isSameOrBefore(moment().startOf("day"));
    };

    const validateAdjustmentsEndDate = (value: moment.Moment) => {
        return value?.isBetween(moment().startOf("day"), moment().add(12, "weeks"));
    };

    const reportTypeIsDischargeAfterTreatment =
        getAnswerToQuestionWithId(questionIds.ReportType) === reportTypeOptions.find(o => o.value === ReportType.DischargeAfterTreatment).value;
    const workClassificationNotAtWork = getAnswerToQuestionWithId(questionIds.WorkClassification) &&
        (getAnswerToQuestionWithId("WorkClassification") as string).startsWith("NotAtWork");
    const askAdjustmentsQuestion = ["ShortTerm", "LongTerm"].indexOf(getAnswerToQuestionWithId(questionIds.Adjustments) as string) > -1;
    const askAdjustmentEndDateQuestion = getAnswerToQuestionWithId(questionIds.Adjustments) === "ShortTerm";
    const askPhasedReturnToWorkDuration = getAnswerToQuestionWithId(questionIds.PhasedReturnToWorkRecommended) === "Yes";
    const fitnessOutcomeAnswer = getAnswerToQuestionWithId(questionIds.FitnessOutcome);
    const isPermanentlyUnfitForWork = fitnessOutcomeAnswer === FitnessOutcome.PermanentlyUnfitForWork;

    const hrAndLineManagers = useHrAndLineManagers(props.consultationAndReferralDetails);

    const physioSpecificRecommendationSegment = (heading: string, recommendationQuestionId: string, timeFrameQuestionId: string) => {
        if (props.readonly) {
            const recommendationQuestionAnswer = getAnswerToQuestionWithId(recommendationQuestionId) as string;
            const timeFrameQuestionAnswer = getAnswerToQuestionWithId(timeFrameQuestionId) as string;

            if (!recommendationQuestionAnswer && !timeFrameQuestionAnswer) {
                return null;
            }
        }

        return (
            <>
                <h4>{heading}</h4>
                <TextQuestion
                    hideDivider
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(recommendationQuestionId)}
                    multiline
                />
                <Divider />
                <TextQuestion
                    hideDivider
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(timeFrameQuestionId)}
                />
                <Divider hidden />
            </>
        );
    };

    if (props.loading) {
        return <Loader active />;
    }

    return (
        <Container className="referral-report">
            <ConsentWithdrawnInfo reportDetails={props.reportDetails} />
            {props.readonly &&
                <>
                    <Segment basic>
                        <strong>Private &amp; Confidential</strong>
                        <div>{getAnswerToQuestionWithId(employeeSeenQuestionId)}</div>
                        <div className="address">{addressString}</div>
                    </Segment>
                </>
            }
            <SavingIndicator display={saving} />
            <AccordionQuestionnaireContainer
                loadQuestionnaire={loadQuestionnaire}
                submitQuestionnaire={(questions) => props.submitQuestionnaire({ questions, eTag: props.report.eTag })}
                readonly={props.readonly}
                questionnaireKey={PHYSIOTHERAPY_REFERRAL_REPORT_KEY}
                extraQuestionContent={props.extraQuestionContent}
                submissionConfirmationText={
                    `You are submitting this report for ${props.employeeRecord?.firstName} ${props.employeeRecord?.lastName}.
                        Are you happy to continue?`
                }
                questionnaireType={QuestionnaireTypes.Report}
            >
                <AccordionQuestionnaireSection
                    title="Referral Outcome Report (Physiotherapy)"
                    id={sections.consultationDetailsSectionId}
                    hideNextButton={true}
                    active={true}
                >
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.HrManager)}
                        answer={hrAndLineManagers}
                        readonly
                    />
                    <Divider />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.EmployeeSeen)}
                        answer={props.employeeRecord ?
                            `${props.employeeRecord?.title} ${props.employeeRecord?.firstName} ${props.employeeRecord?.lastName}` :
                            ""
                        }
                        readonly
                    />
                    <Divider />
                    <OldDateQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.Dob)}
                        answer={props.employeeRecord?.dateOfBirth?.format("YYYY-MM-DD")}
                        readonly
                    />
                    <Divider />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.EmployeeId)}
                        answer={props.consultationAndReferralDetails?.employeeId}
                        readonly
                    />
                    <Divider />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.EmployerAndDivision)}
                        answer={props.employeeRecord &&
                            [
                                props.employeeRecord.customerName,
                                props.employeeRecord.orgStructureOne && props.employeeRecord.orgStructureOne.name
                            ].filter(t => t).join(" - ")
                        }
                        readonly
                    />
                    <Divider />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.JobTitle)}
                        answer={props.employeeRecord?.jobTitle}
                        readonly
                    />
                    <Divider />
                    <OldDateQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.DateOfConsultation)}
                        answer={props.consultationAndReferralDetails?.consultationDate.format("YYYY-MM-DD HH:mm")}
                        readonly
                    />
                    <Divider />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.ConsultationType)}
                        answer={props.consultationAndReferralDetails?.consultationType}
                        readonly
                    />
                    <Divider />
                    <DropdownQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.ReportType)}
                        options={reportTypeOptions.sort((a, b) => a.text.localeCompare(b.text))}
                        mandatory
                    />
                    {!props.readonly &&
                        <Grid>
                            <Grid.Row>
                                <Grid.Column width={16}>
                                    <Container textAlign="right">
                                        {props.ohCase && props.consultationAndReferralDetails &&
                                            <>
                                                <a
                                                    href={`/customers/${props.customerId}/employeeRecords/${props.ohCase.employeeRecordId}`
                                                        + "/clinicalHistory"}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <Button type="button" content="View Clinical History" />
                                                </a>
                                                <a
                                                    href={`/customers/${props.customerId}/employeeRecords/${props.ohCase.employeeRecordId}`
                                                        + `/ohCases/${props.ohCaseId}/${props.consultationAndReferralDetails.consultationTaskId}`
                                                        + "/physiotherapyAssessmentConsultation/"}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <Button type="button" content="View Clinical Notes" />
                                                </a>
                                            </>
                                        }
                                    </Container>
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>}
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection title="Summary" id={sections.summarySectionId} hideNextButton={true} active={true}>
                    <DropdownQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.WorkClassification)}
                        options={workClassificationOptions.sort((a, b) => a.text.localeCompare(b.text)).map(o => ({ text: o.text, value: o.value }))}
                        mandatory
                    />
                    <Divider />
                    {workClassificationNotAtWork &&
                        <>
                            <OldDateQuestion
                                hideDivider
                                inline
                                questionConfig={getQuestionConfig(questionIds.FirstDayOfAbsence)}
                                readonly={props.readonly}
                                mandatory={workClassificationNotAtWork}
                                validators={[
                                    createValidator((value: moment.Moment) => isDateInThePast(value), "This date must be in the past")
                                ]}
                            />
                            <Divider />
                        </>
                    }
                    <DropdownQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.FitnessOutcome)}
                        options={fitnessOutcomeOptions.sort((a, b) => a.text.localeCompare(b.text))}
                        mandatory
                    />
                    <Divider />
                    {workClassificationNotAtWork &&
                        <>
                            <OldDateQuestion
                                hideDivider
                                inline
                                questionConfig={getQuestionConfig(questionIds.AdvisedTargetReturnToWorkDate)}
                                readonly={props.readonly}
                                mandatory={workClassificationNotAtWork && !isPermanentlyUnfitForWork}
                                validators={[
                                    createValidator((value: moment.Moment) => !isDateInThePast(value), "This date must not be in the past")
                                ]}
                            />
                            <Divider />
                        </>
                    }
                    <DropdownQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.Adjustments)}
                        options={adjustmentOptions}
                        mandatory
                    />
                    <Divider />
                    {askAdjustmentsQuestion &&
                        <>
                            <MultiCheckboxQuestion
                                hideDivider
                                inline
                                questionConfig={getQuestionConfig(questionIds.AdjustmentsRecommended)}
                                readonly={props.readonly}
                                mandatory={askAdjustmentsQuestion}
                                hidden={props.readonly}
                            >
                                {(Object.keys(adjustmentsRecommendedOptions) as (keyof typeof adjustmentsRecommendedOptions)[]).map(k =>
                                    <MultiCheckbox key={k} value={k} label={adjustmentsRecommendedOptions[k]} />)}
                            </MultiCheckboxQuestion>
                            <Divider />
                        </>
                    }
                    {askAdjustmentsQuestion && props.readonly &&
                        <>
                            <Grid>
                                <Grid.Row>
                                    <Grid.Column mobile="16" tablet="3" computer="3" className="inline-question">
                                        Adjustments recommended
                                    </Grid.Column>
                                    <Grid.Column mobile="16" tablet="13" computer="13">
                                        <List as="ul">
                                            {(getAnswerToQuestionWithId(
                                                questionIds.AdjustmentsRecommended
                                            ) as (keyof typeof adjustmentsRecommendedOptions)[] || [])
                                                .map(a => <List.Item as="li" key={a}>{adjustmentsRecommendedOptions[a]}</List.Item>)}
                                        </List>
                                    </Grid.Column>
                                </Grid.Row>
                            </Grid>
                            <Divider />
                        </>
                    }
                    {askAdjustmentEndDateQuestion &&
                        <>
                            <OldDateQuestion
                                hideDivider
                                mandatory={askAdjustmentEndDateQuestion}
                                inline
                                questionConfig={getQuestionConfig(questionIds.AdjustmentsEndDate)}
                                validators={[
                                    createValidator((value: moment.Moment) => !isDateInThePast(value), "Must not be in the past"),
                                    createValidator((value: moment.Moment) => validateAdjustmentsEndDate(value),
                                        "Cannot be more than 12 weeks in the future")
                                ]}
                                readonly={props.readonly}
                            />
                            <Divider />
                        </>
                    }
                    {workClassificationNotAtWork &&
                        <>
                            <RadioButtonQuestion
                                hideDivider
                                inline
                                questionConfig={getQuestionConfig(questionIds.PhasedReturnToWorkRecommended)}
                                readonly={props.readonly}
                                mandatory
                            >
                                <RadioButton label="Yes" value="Yes" />
                                <RadioButton label="No" value="No" />
                            </RadioButtonQuestion>
                            <Divider />
                        </>
                    }
                    {askPhasedReturnToWorkDuration &&
                        <>
                            <DropdownQuestion
                                hideDivider
                                inline
                                readonly={props.readonly}
                                questionConfig={getQuestionConfig(questionIds.DurationOfPhasedReturn)}
                                options={phasedReturnOptions}
                                mandatory={askPhasedReturnToWorkDuration}
                            />
                            <Divider />
                        </>
                    }
                    <TextQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.OnwardReferral)}
                    />
                    <Divider />
                    <RadioButtonQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.GpSpecialistReportRequested)}
                        readonly={props.readonly}
                        mandatory
                    >
                        <RadioButton label="Yes" value="Yes" />
                        <RadioButton label="No" value="No" />
                    </RadioButtonQuestion>
                    <Divider />
                    <RadioButtonQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.EqualityActLikelyToApply)}
                        readonly={props.readonly}
                        mandatory
                    >
                        <RadioButton label="Yes" value="Yes" />
                        <RadioButton label="No" value="No" />
                    </RadioButtonQuestion>
                    <Divider />
                    <TextQuestion
                        hideDivider
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.JustificationForEqualityActOpinion)}
                        multiline
                        mandatory
                    />
                    <Divider />
                    <TextQuestion
                        hideDivider
                        inline
                        readonly={true}
                        questionConfig={getQuestionConfig(questionIds.ReasonForReferral)}
                        answer={props.consultationAndReferralDetails?.reasonForReferral}
                    />
                    <Divider />
                    <TextQuestion
                        hideDivider
                        inline
                        readonly={true}
                        questionConfig={getQuestionConfig(questionIds.SelfManagementWithAdviceAndExercises)}
                        answer={props.consultationAndReferralDetails?.selfManagementWithAdviceAndExercises}
                    />
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    id={sections.backgroundReasonsForReferralSectionId}
                    title="Background and Reason for Referral"
                    hideNextButton={true}
                    active={true}
                >
                    <TextQuestion
                        hideDivider
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.BackgroundReasonsForReferral)}
                        multiline
                    />
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    id={sections.assessmentAndOpinionsSectionId}
                    title="Assessment and Opinions"
                    hideNextButton={true}
                    active={true}
                >
                    <TextQuestion
                        hideDivider
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.AssessmentAndOpinions)}
                        multiline
                    />
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    id={sections.recommendationsAndSummarySectionId}
                    title="Information/Answers"
                    hideNextButton={true}
                    active={true}
                >
                    {props.consultationAndReferralDetails &&
                        props.consultationAndReferralDetails.informationRequiredSection &&
                        props.consultationAndReferralDetails.informationRequiredSection.some(q => !!q.answer) &&
                        <Grid.Row>
                            <List className={"triage-questionnaire-answers information-required"}>
                                {props.consultationAndReferralDetails.informationRequiredSection.filter(q => q.answer).map(question => (
                                    <QuestionnaireAnswer
                                        // Display question text (which will include custom question text)
                                        // for checkbox questions, and answer text for text question
                                        question={{ ...question, answer: question.id === questionIds.AdditionalInfo ? question.answer : question.question }}
                                        key={question.id}
                                    />))}
                            </List>
                        </Grid.Row>}
                    <TextQuestion
                        hideDivider
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.RecommendationsAndSummary)}
                        multiline
                    />
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    id={sections.physiotherapySpecificRecommendationsSectionId}
                    title="Physiotherapy Specific Recommendations"
                    hideNextButton={true}
                    active={true}
                >
                    {physioSpecificRecommendationSegment(
                        "Standing and walking",
                        questionIds.StandingAndWalkingRecommendations,
                        questionIds.StandingAndWalkingTimeFrame
                    )}
                    {physioSpecificRecommendationSegment(
                        "Squatting, kneeling & stepping",
                        questionIds.SquattingKneelingSteppingRecommendations,
                        questionIds.SquattingKneelingSteppingTimeFrame
                    )}
                    {physioSpecificRecommendationSegment(
                        "Grip strength, working at shoulder height or above head",
                        questionIds.GripStrengthWorkingAtShoulderHeightOrAboveHeadRecommendations,
                        questionIds.GripStrengthWorkingAtShoulderHeightOrAboveHeadTimeFrame
                    )}
                    {physioSpecificRecommendationSegment(
                        "Lifting & carrying",
                        questionIds.LiftingCarryingRecommendations,
                        questionIds.LiftingCarryingTimeFrame
                    )}
                    {physioSpecificRecommendationSegment(
                        "Pushing & pulling",
                        questionIds.PushingPullingRecommendations,
                        questionIds.PushingPullingTimeFrame
                    )}
                    {physioSpecificRecommendationSegment(
                        "Seated activities; desk work & driving",
                        questionIds.SeatedActivitiesDeskWorkAndDrivingRecommendations,
                        questionIds.SeatedActivitiesDeskWorkAndDrivingTimeFrame
                    )}
                    {physioSpecificRecommendationSegment(
                        "PMVA / C&R",
                        questionIds.PMVA_CAndRRecommendations,
                        questionIds.PMVA_CAndRTimeFrame
                    )}
                    {physioSpecificRecommendationSegment(
                        "Patient Handling",
                        questionIds.PatientHandlingRecommendations,
                        questionIds.PatientHandlingTimeFrame
                    )}
                </AccordionQuestionnaireSection>
                {reportTypeIsDischargeAfterTreatment &&
                    <AccordionQuestionnaireSection
                        id={sections.dischargeAfterTreatmentInformation}
                        title="Discharge after Treatment Information"
                        hideNextButton={true}
                        active={true}
                    >
                        <TextQuestion
                            hideDivider
                            inline
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(questionIds.NumberOfSessionsIncludingInitialAssessment)}
                            mandatory
                        />
                        <Divider />
                        <MultiCheckboxQuestion
                            hideDivider
                            inline
                            questionConfig={getQuestionConfig(questionIds.ManagementDetails)}
                            readonly={props.readonly}
                            mandatory={askAdjustmentsQuestion}
                            hidden={props.readonly}
                        >
                            {(Object.keys(managementDetailsOptions) as (keyof typeof managementDetailsOptions)[]).map(k =>
                                <MultiCheckbox key={k} value={k} label={managementDetailsOptions[k]} />)}
                        </MultiCheckboxQuestion>
                        <Divider />
                        {props.readonly &&
                            <>
                                <Grid>
                                    <Grid.Row>
                                        <Grid.Column mobile="16" tablet="3" computer="3" className="inline-question">
                                            Management Details*
                                        </Grid.Column>
                                        <Grid.Column mobile="16" tablet="13" computer="13">
                                            <List as="ul">
                                                {(getAnswerToQuestionWithId(questionIds.ManagementDetails) as (keyof typeof managementDetailsOptions)[] || [])
                                                    .map(a => <List.Item as="li" key={a}>{managementDetailsOptions[a]}</List.Item>)}
                                            </List>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                                <Divider />
                            </>
                        }
                        <DropdownQuestion
                            hideDivider
                            inline
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(questionIds.Outcome)}
                            options={outcomeOptions}
                            mandatory
                        />
                        <Divider />
                        <DropdownQuestion
                            hideDivider
                            inline
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(questionIds.WorkStatusAtInitialAssessment)}
                            options={workStatusAtInitialAssessmentOptions}
                            mandatory
                        />
                        <Divider />
                        <DropdownQuestion
                            hideDivider
                            inline
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(questionIds.WorkStatusAtDischarge)}
                            options={workStatusAtDischargeOptions}
                            mandatory
                        />
                        <Divider />
                        <TextQuestion
                            hideDivider
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(questionIds.QuestionnaireScoresAtInitialAssessment)}
                            multiline
                        />
                        <Divider />
                        <TextQuestion
                            hideDivider
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(questionIds.QuestionnaireScoresAtDischarge)}
                            multiline
                        />
                    </AccordionQuestionnaireSection>
                }
                {props.extraSections?.map((s, i) =>
                    (<AccordionQuestionnaireSection key={i} title={s.title} id={s.id} hideNextButton={true} active={true} neverHide={true}>
                        {s.content}
                    </AccordionQuestionnaireSection>))
                }
                {props.readonly &&
                    <>
                        <AccordionQuestionnaireSection
                            id={sections.reportDetailsSectionId}
                            title="Report details"
                            hideNextButton={true}
                            active={true}
                        >
                            <OldDateQuestion hideDivider inline readonly={true} questionConfig={getQuestionConfig(questionIds.ReportDate)} />
                            <Divider />
                            <Grid>
                                <Grid.Row>
                                    <Grid.Column width="3">Occupational Health Adviser</Grid.Column>
                                    <Grid.Column width="5" floated="left">
                                        {props.consultationAndReferralDetails?.occupationalHealthAdviserName}
                                    </Grid.Column>
                                </Grid.Row>
                                <Grid.Row>
                                    <Grid.Column width="3">Clinician Qualification</Grid.Column>
                                    <Grid.Column width="5" floated="left">
                                        {props.consultationAndReferralDetails?.occupationalHealthAdviserQualification}
                                    </Grid.Column>
                                </Grid.Row>
                            </Grid>
                        </AccordionQuestionnaireSection>
                        <EmployeeReleaseReportWithCommentsInfo reportDetails={props.reportDetails} />
                        <Divider />
                        <p>
                            Printing Disclaimer:  This report contains personal identifiable information and sensitive health details.
                            It is possible to print this report or create a pdf of this report using your in-browser printing however
                            we would strongly recommend that you only view this report in the system and avoid printing where ever possible.
                            If you do choose to print or export this information then you do so at your own risk and you must take all necessary
                            measures to protect the information contained within this report and ensure that it is only viewed by authorised
                            personnel within your own organisation.
                        </p>
                    </>
                }
            </AccordionQuestionnaireContainer>
        </Container>
    );
};
